import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, Classes, Intent, Dialog } from "@blueprintjs/core";
import { DragDropContext, DropResult, DraggableLocation } from "react-beautiful-dnd";
import { cloneDeep } from "lodash";
import {
    InputInstructionHasDependency,
    SourceDialogContext,
    SourceDialogCurrentProjectContext
} from "src/utils/context";
import { UserProject } from "src/models/UserProjects";
import { ListState, InstructionTo, ListId } from "src/models/TemplateCreationList";
import InputManuallySet from "./InputManuallySet";
import InputDependecySet from "./InputDependecySet";
import InputHardcodeSet from "./InputHardcodeSet";
import { UserInputs } from "src/models/UserInputs";
import { Dictionary } from "src/utils/utils";

const defaultListState = { manual: [], dependency: [], hardcode: [] };

type Props = {
    availableInstructions: UserProject[];
    processInputs: UserInputs;
    setProcessInputs: React.Dispatch<React.SetStateAction<UserInputs>>;
    masterList: Dictionary<ListState>;
    setMasterList: React.Dispatch<React.SetStateAction<Dictionary<ListState>>>;
};

export default function SourceDialog({
    availableInstructions,
    processInputs,
    setProcessInputs,
    masterList,
    setMasterList
}: Props) {
    const [open, setOpen] = useContext(SourceDialogContext);
    const [currentProject] = useContext(SourceDialogCurrentProjectContext);
    const [instructionHasDependecy] = useContext(InputInstructionHasDependency);
    const [lists, setLists] = useState<ListState>(defaultListState);
    const listRef = useRef(lists);
    const processInputsRef = useRef(processInputs);

    const setInitialListState = useCallback(
        currentProject => {
            const manual: InstructionTo[] = Object.keys(currentProject.userInputs).map(u => ({ original: u }));
            const updated = { manual: manual, dependency: [], hardcode: [] };
            setLists(updated);
            listRef.current = updated;
            processInputsRef.current = {};
        },
        [setLists]
    );

    // project changed for which the input source is being set has changed
    useEffect(() => {
        if (currentProject) {
            // if the instruction is in the master list (identified by the project id)
            // then use these lists otherwise create an intial state where all user inputs
            // are in the manual state.
            if (masterList[currentProject.projectId]) {
                //!todo set by instructionID instead of projectId
                setLists(masterList[currentProject.projectId]);
            } else {
                setInitialListState(currentProject);
            }
        } else {
            setLists(defaultListState);
        }
    }, [currentProject, masterList, setInitialListState]);

    const setProcessInputsPreviousState = useCallback(() => {
        setProcessInputs(processInputsRef.current);
    }, [setProcessInputs]);

    const getList = useCallback(
        (id: ListId) => {
            return lists[id];
        },
        [lists]
    );

    const reorder = useCallback((list: any[], startIndex: number, endIndex: number) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    }, []);

    const move = useCallback(
        (
            sourceId: ListId,
            destinationId: ListId,
            droppableSource: DraggableLocation,
            droppableDestination: DraggableLocation
        ) => {
            const sourceClone = cloneDeep(getList(sourceId));
            const destClone = cloneDeep(getList(destinationId));
            const [removed] = sourceClone.splice(droppableSource.index, 1);

            // clear mappings if a move happens
            removed.mapsTo = undefined;
            removed.value = undefined;

            destClone.splice(droppableDestination.index, 0, removed);
            const result: ListState = cloneDeep(lists);
            result[sourceId] = sourceClone;
            result[destinationId] = destClone;
            setLists(result);
        },
        [getList, lists, setLists]
    );

    const handleDragEnd = useCallback(
        (result: DropResult) => {
            const { source, destination } = result;
            if (!destination) return;

            const sourceId = source.droppableId as ListId;
            const destinationId = destination.droppableId as ListId;
            if (sourceId === destinationId) {
                const listsCopy = cloneDeep(lists);
                const items = reorder(getList(sourceId), source.index, destination.index);
                listsCopy[sourceId] = items;
                setLists(listsCopy);
            } else {
                move(sourceId, destinationId, source, destination);
            }
        },
        [getList, lists, move, reorder, setLists]
    );

    const handleClose = useCallback(() => {
        setOpen(false);
        // setListToPreviousState();

        setProcessInputsPreviousState();
    }, [setOpen, setProcessInputsPreviousState]);

    const handleSet = useCallback(() => {
        listRef.current = lists;
        processInputsRef.current = processInputs;
        if (currentProject) {
            // update only the list of the currently selected project
            //!todo: update to use instructionId instead of projectId
            setMasterList(ml => ({ ...ml, [currentProject.projectId]: lists }));
        }
        setOpen(false);
    }, [currentProject, lists, processInputs, setMasterList, setOpen]);

    const handleReset = useCallback(() => {
        setInitialListState(currentProject);
        setProcessInputs({});
        processInputsRef.current = {};
    }, [currentProject, setInitialListState, setProcessInputs]);

    const setList = useCallback((listName: keyof ListState, newList: InstructionTo[]) => {
        // grab one of the hardcoded, manual, or dependency list and only update it.
        setLists(l => ({ ...l, [listName]: newList }));
    }, []);

    return (
        <Dialog
            enforceFocus={false}
            isOpen={open}
            icon="arrow-right"
            onClose={handleClose}
            title="Set Dependecy Data Source"
        >
            <div className={Classes.DIALOG_BODY}>
                <div className="instructions">
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <InputManuallySet
                            lists={lists}
                            setLists={setLists}
                            list={lists.manual}
                            setList={setList}
                            processInputs={processInputs}
                            setProcessInputs={setProcessInputs}
                        />
                        {instructionHasDependecy && (
                            <InputDependecySet
                                lists={lists}
                                setLists={setLists}
                                list={lists.dependency}
                                setList={setList}
                                availableInstructions={availableInstructions}
                            />
                        )}
                        <InputHardcodeSet lists={lists} setLists={setLists} list={lists.hardcode} setList={setList} />
                    </DragDropContext>
                </div>
            </div>
            <div className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    {/* the set button is the only way to close the modal and not reset state*/}
                    <Button text="Save" intent={Intent.PRIMARY} onClick={handleSet} />
                    <Button text="Reset" onClick={handleReset} />
                    <Button text="Close" onClick={handleClose} />
                </div>
            </div>
        </Dialog>
    );
}
